import React, { useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import ButtonPrimary from "../Button/ButtonPrimary";
import countryList from "country-list";
import Input from "../Input/Input";

const ALPHA3_TO_ALPHA2 = {
  ETH: "ET",
  USA: "US",
  GBR: "GB",
  CAN: "CA",
  DEU: "DE",
  FRA: "FR",
  IND: "IN",
  KEN: "KE",
  NGA: "NG",
  ZAF: "ZA",
  EGY: "EG",
  SAU: "SA",
  ARE: "AE",
  CHN: "CN",
  JPN: "JP",
  AUS: "AU",
  BRA: "BR",
  RUS: "RU",
  ITA: "IT",
  ESP: "ES",
  NLD: "NL",
  SWE: "SE",
  NOR: "NO",
  DNK: "DK",
  FIN: "FI",
  IRL: "IE",
  POL: "PL",
  TUR: "TR",
  PAK: "PK",
  BGD: "BD",
  SUD: "SD",
  TZA: "TZ",
  UGA: "UG",
  GHA: "GH",
  CIV: "CI",
  SEN: "SN",
  MAR: "MA",
  TUN: "TN",
  DZA: "DZ",
  LBY: "LY",
};

const getCountryName = (code) => {
  if (!code) return "";
  const alpha2 = ALPHA3_TO_ALPHA2[code?.toUpperCase()] || code;
  return countryList.getName(alpha2) || code;
};

const EditableField = ({ label, value, onChange, type = "text", options }) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-neutral-500">
      {label}
    </label>
    {options ? (
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="block px-3 py-2 w-full text-sm font-medium bg-white rounded-lg border border-neutral-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    ) : (
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="text-sm"
      />
    )}
  </div>
);

const PassportExtractionModal = ({
  isOpen,
  onClose,
  data,
  passengerIndex,
  onAccept,
  onReject,
  passengerTypeCode,
  company,
}) => {
  const isAdultOrLabor =
    passengerTypeCode === "ADT" || passengerTypeCode === "LBR";

  const [edited, setEdited] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    documentNumber: "",
    birthDate: "",
    expirationDate: "",
    nationality: "",
    sex: "",
    issuingState: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    if (data) {
      setEdited({
        title: data.sex === "M" ? "Mr" : "Ms",
        firstName: data.firstName || "",
        middleName: data.middleName || "",
        lastName: data.lastName || "",
        documentNumber: data.documentNumber || "",
        birthDate: data.birthDate || "",
        expirationDate: data.expirationDate || "",
        nationality: getCountryName(data.nationality) || data.nationality || "",
        sex: data.sex || "",
        issuingState: data.issuingState || "",
        email: isAdultOrLabor ? company?.user?.email || "" : "",
        phone: isAdultOrLabor ? (company?.user?.phone || "").slice(4) : "",
      });
    }
  }, [
    data,
    isOpen,
    isAdultOrLabor,
    company?.user?.email,
    company?.user?.phone,
  ]);

  if (!data) return null;

  const updateField = (field, value) => {
    setEdited((prev) => ({ ...prev, [field]: value }));
  };

  const handleAccept = () => {
    onAccept(
      {
        ...data,
        title: edited.title,
        firstName: edited.firstName,
        middleName: edited.middleName,
        lastName: edited.lastName,
        documentNumber: edited.documentNumber,
        birthDate: edited.birthDate,
        expirationDate: edited.expirationDate,
        nationality: edited.nationality,
        sex: edited.sex,
        ...(isAdultOrLabor && {
          email: edited.email,
          phone: edited.phone,
        }),
      },
      passengerIndex
    );
    onClose();
  };

  const handleReject = () => {
    onReject?.();
    onClose();
  };

  const fullName = [
    edited.title,
    edited.firstName,
    edited.middleName,
    edited.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[100] overflow-y-auto"
        onClose={onClose}
      >
        <div className="flex justify-center items-center px-4 py-8 min-h-screen">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 backdrop-blur-md bg-black/50" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-90 translate-y-4"
            enterTo="opacity-100 scale-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100 translate-y-0"
            leaveTo="opacity-0 scale-90 translate-y-4"
          >
            <div className="overflow-hidden relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl">
              {/* Header */}
              <div className="relative p-4 bg-main">
                <div className="absolute inset-0 opacity-30"></div>

                <div className="flex relative gap-4 items-center">
                  <div>
                    <Dialog.Title
                      as="h3"
                      className="text-xl font-bold text-white"
                    >
                      Passport Verified
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-white/80">
                      Passenger {passengerIndex + 1} • Data successfully
                      extracted
                    </p>
                  </div>
                </div>

                {/* Full Name Banner */}
                {fullName && (
                  <div className="px-2 py-2 mt-3 rounded-xl border backdrop-blur-sm bg-white/15 border-white/20">
                    <p className="text-xs font-medium tracking-wider uppercase text-white/70">
                      Full Name
                    </p>
                    <p className="mt-1 text-lg font-bold text-white">
                      {fullName}
                    </p>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="px-6 py-6">
                <p className="px-4 py-3 mb-4 text-sm text-amber-800 bg-amber-50 rounded-lg border border-amber-200">
                  <span className="font-semibold">
                    Please verify the extracted data.
                  </span>{" "}
                  You can edit any field before accepting.
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <EditableField
                    label="Title"
                    value={edited.title}
                    onChange={(v) => updateField("title", v)}
                    options={[
                      { value: "", label: "Select" },
                      { value: "Mr", label: "Mr" },
                      { value: "Ms", label: "Ms" },
                    ]}
                  />
                  <EditableField
                    label="First Name"
                    value={edited.firstName}
                    onChange={(v) => updateField("firstName", v)}
                  />
                  <EditableField
                    label="Middle Name"
                    value={edited.middleName}
                    onChange={(v) => updateField("middleName", v)}
                  />
                  <EditableField
                    label="Last Name"
                    value={edited.lastName}
                    onChange={(v) => updateField("lastName", v)}
                  />
                  {isAdultOrLabor && (
                    <>
                      <EditableField
                        label="Phone"
                        value={edited.phone}
                        onChange={(v) => updateField("phone", v)}
                      />
                      <EditableField
                        label="Email"
                        type="email"
                        value={edited.email}
                        onChange={(v) => updateField("email", v)}
                      />
                    </>
                  )}
                  <EditableField
                    label="Document Number"
                    value={edited.documentNumber}
                    onChange={(v) => updateField("documentNumber", v)}
                  />
                  <EditableField
                    label="Date of Birth"
                    value={edited.birthDate}
                    onChange={(v) => updateField("birthDate", v)}
                    type="date"
                  />
                  <EditableField
                    label="Expiry Date"
                    value={edited.expirationDate}
                    onChange={(v) => updateField("expirationDate", v)}
                    type="date"
                  />
                  <EditableField
                    label="Nationality"
                    value={edited.nationality}
                    onChange={(v) => updateField("nationality", v)}
                    options={[
                      { value: "", label: "Select country" },
                      ...countryList.getNames().map((name) => ({
                        value: name,
                        label: name,
                      })),
                    ]}
                  />
                  <EditableField
                    label="Gender"
                    value={edited.sex}
                    onChange={(v) => updateField("sex", v)}
                    options={[
                      { value: "", label: "Select" },
                      { value: "M", label: "Male" },
                      { value: "F", label: "Female" },
                    ]}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 px-6 pt-2 pb-6">
                <button
                  type="button"
                  onClick={handleReject}
                  className="flex-1 px-6 py-3.5 text-sm font-semibold rounded-xl transition-all duration-200 text-neutral-600 bg-neutral-100 hover:bg-neutral-200 hover:text-neutral-800 active:scale-[0.98]"
                >
                  Reject
                </button>
                <ButtonPrimary
                  onClick={handleAccept}
                  className="flex-1 !py-3.5 !rounded-xl !text-sm !font-semibold shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 active:scale-[0.98] transition-all duration-200"
                >
                  Accept & Fill Form
                </ButtonPrimary>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PassportExtractionModal;
